import Layout from "@components/layout/layout";
import LoadingSkeletonUntyped from "@shared/LoadingSkeleton";
import ReactTableTrademoUntyped from "@shared/trademo-datatable/react-table-trademo";
import { captureException } from "@sentry/react";
import commaFormat from "@utils/commaFormat";
import { ERROR_MSG } from "@utils/enum";
import moment from "moment";
import { useCallback, useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { useHistory } from "react-router-dom";
import { toast } from "react-toastify";
import { FROM_BULK_LOGS, RESULTS_PAGE_SIZE, RESULTS_PAGE_SIZE_OPTIONS } from "../constants";
import { getApiErrorMessage, getBulkSearchLogs } from "../services";
import type { BulkLogStatus, BulkNavigationState, BulkSearchLog } from "../types";
import { LOG_SORT_OPTIONS, LOG_STATUS_OPTIONS, LOGS_FETCH_SIZE, UPLOADED_ON_SORT_KEY } from "./constants";
import LogStatusFilter from "./LogStatusFilter";

const LoadingSkeleton = LoadingSkeletonUntyped as unknown as (props: Record<string, unknown>) => ReactElement;
const ReactTableTrademo = ReactTableTrademoUntyped as unknown as (props: Record<string, unknown>) => ReactElement;

type SortOrder = "asc" | "desc";

type LogRow = BulkSearchLog & { s_no: number };

const formatDate = (value?: string | null) => (value ? moment(value).format("DD MMM YYYY, hh:mm A") : "-");

const BulkSearchLogs = () => {
  const history = useHistory();
  const [logs, setLogs] = useState<LogRow[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState<BulkLogStatus[]>([]);
  const [showFilters, setShowFilters] = useState(false);
  const [sortOrder, setSortOrder] = useState<SortOrder>("desc");
  const [currentPage, setCurrentPage] = useState(1);
  const [perPage, setPerPage] = useState(RESULTS_PAGE_SIZE);

  // Mirrors of state read inside async loaders, so a load started before a
  // re-render does not act on stale values.
  const logsRef = useRef<LogRow[]>([]);
  const totalRef = useRef(0);
  const queryVersionRef = useRef(0);
  const isLoadingMoreRef = useRef(false);

  // ReactTableTrademo pages over the rows it is given and only enables "next"
  // when that page is already loaded, so rows are fetched ahead of the user:
  // enough to cover the current page plus the one after it.
  const ensureLoaded = useCallback(
    async (target: number, reset = false) => {
      if (!reset && isLoadingMoreRef.current) return;
      const version = reset ? ++queryVersionRef.current : queryVersionRef.current;
      isLoadingMoreRef.current = true;
      if (reset) {
        logsRef.current = [];
        totalRef.current = 0;
        setLoading(true);
      }
      try {
        while (reset || logsRef.current.length < Math.min(target, totalRef.current)) {
          reset = false;
          const from = logsRef.current.length;
          const data = await getBulkSearchLogs({ status: statusFilter, sortOrder, from, size: LOGS_FETCH_SIZE });
          if (version !== queryVersionRef.current) return;
          const page = (data?.logs ?? []).map((log, index) => ({ ...log, s_no: from + index + 1 }));
          logsRef.current = [...logsRef.current, ...page];
          totalRef.current = data?.total_count ?? logsRef.current.length;
          setLogs(logsRef.current);
          setTotalCount(totalRef.current);
          if (!page.length) break;
        }
      } catch (error) {
        captureException(error);
        toast.error(getApiErrorMessage(error, ERROR_MSG));
      } finally {
        if (version === queryVersionRef.current) {
          isLoadingMoreRef.current = false;
          setLoading(false);
        }
      }
    },
    [statusFilter, sortOrder]
  );

  // A new filter or sort is a new query: start again from the first page.
  useEffect(() => {
    setCurrentPage(1);
    ensureLoaded(2 * perPage, true);
    // perPage is deliberately left out: a page-size change keeps the query and
    // only needs more rows, which onTableObjectChange handles.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ensureLoaded]);

  const handleTableChange = (table: { currentPage: number; perPage: number }) => {
    setPerPage(table.perPage);
    setCurrentPage(table.currentPage + 1);
    ensureLoaded((table.currentPage + 2) * table.perPage);
  };

  const handleStatusFilterChange = useCallback((selected: BulkLogStatus[]) => setStatusFilter(selected), []);

  const closeFilters = useCallback(() => setShowFilters(false), []);

  const handleSort = (_column: string, direction: SortOrder) => setSortOrder(direction);

  // A confirmed search has nothing left to review, so it opens straight on its
  // confirmed companies; everything else opens on the match results.
  const openLog = (log: BulkSearchLog) => {
    const state: BulkNavigationState = { totalRecords: log.total_records, from: FROM_BULK_LOGS };
    const pathname = log.status === "CONFIRMED" ? `/company-search/bulk/${log.search_id}/companies` : `/company-search/bulk/${log.search_id}`;
    history.push({ pathname, state });
  };

  const columns = [
    {
      name: "Log ID",
      minWidth: "260px !important",
      maxWidth: "260px !important",
      selector: (row: BulkSearchLog) => (
        <button type="button" onClick={() => openLog(row)} title={row.search_id} className="tw-block tw-max-w-full tw-truncate tw-border-none tw-bg-transparent tw-p-0 tw-text-left tw-text-sm tw-font-medium tw-text-primary-purple-600 hover:tw-underline">
          {row.search_id}
        </button>
      ),
    },
    {
      name: "File Name",
      minWidth: "320px !important",
      maxWidth: "320px !important",
      selector: (row: BulkSearchLog) => (
        <div className="tw-truncate" title={row.file_name}>
          {row.file_name || "-"}
        </div>
      ),
    },
    {
      name: "Uploaded On",
      key: UPLOADED_ON_SORT_KEY,
      sortable: true,
      minWidth: "200px !important",
      maxWidth: "200px !important",
      selector: (row: BulkSearchLog) => <div>{formatDate(row.created_at)}</div>,
    },
    {
      name: "Companies",
      minWidth: "120px !important",
      maxWidth: "120px !important",
      selector: (row: BulkSearchLog) => <div>{typeof row.total_records === "number" ? commaFormat(row.total_records) : "-"}</div>,
    },
    {
      name: "Status",
      minWidth: "180px !important",
      maxWidth: "180px !important",
      selector: (row: BulkSearchLog) => {
        const status = LOG_STATUS_OPTIONS.find((option) => option.key === row.status);
        return <span className={`tw-inline-block tw-whitespace-nowrap tw-rounded-full tw-px-2.5 tw-py-0.5 tw-text-xs tw-font-medium ${status?.className ?? "tw-bg-gray-100 tw-text-gray-700"}`}>{status?.label ?? row.status}</span>;
      },
    },
  ];

  const renderTable = () => {
    if (loading) return <LoadingSkeleton lines={10} lineHeight={"40px"} width={"100%"} shape="rectangle" widthArr={["100%", "100%", "100%", "100%"]} />;

    if (!logs.length) {
      return (
        <div className="tw-py-16 tw-text-center tw-text-sm tw-text-gray-500">
          {statusFilter.length ? (
            <>
              <p className="tw-mb-3">No bulk searches match the selected status.</p>
              <button type="button" onClick={() => handleStatusFilterChange([])} className="btn btn-primary btn-sm trademo-new-btn">
                Clear Filter
              </button>
            </>
          ) : (
            "No bulk searches yet. Upload a file on the company search page to get started."
          )}
        </div>
      );
    }

    return (
      <ReactTableTrademo
        // Remounting on a new query resets the table's internal page counters,
        // which it otherwise only updates on its own navigation.
        key={`${sortOrder}-${statusFilter.join(",")}`}
        columns={columns}
        data={logs}
        paginationTotalRows={totalCount}
        paginationPerPage={perPage}
        paginationDefaultPage={currentPage}
        options={RESULTS_PAGE_SIZE_OPTIONS}
        selectedData={[]}
        onSelectedRowsChange={() => null}
        onChangePage={() => null}
        onChangeRowsPerPage={(size: number) => setPerPage(size)}
        onTableObjectChange={handleTableChange}
        onSort={handleSort}
        defaultSortField={UPLOADED_ON_SORT_KEY}
        sortOrderValue={sortOrder}
        shipmentDefaultSortValue={LOG_SORT_OPTIONS}
        hideSorting
        hideBorder
        hideSelection
      />
    );
  };

  return (
    <Layout type="companySearch" pageTitle="Bulk Search Logs | Trademo Intel" setNotification={() => null}>
      <div className="tw-text-gray-900">
        <div className="tw-flex tw-items-center tw-justify-between tw-gap-4 tw-px-5 tw-py-4">
          <button type="button" onClick={() => history.push("/company-search")} className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-base tw-text-gray-max">
            <span className="material-symbols-outlined tw-font-semibold tw-text-2xl tw-leading-none">arrow_left_alt</span>
            Go back
          </button>

          <div className="tw-relative tw-flex tw-items-center tw-gap-2">
            {statusFilter.length > 0 && (
              <button type="button" onClick={() => handleStatusFilterChange([])} className="tw-flex tw-items-center tw-gap-1 tw-border-none tw-bg-transparent tw-p-0 tw-text-sm tw-font-medium tw-text-primary-purple-600">
                Clear filters
                <span className="material-symbols-outlined tw-text-base tw-leading-none">close</span>
              </button>
            )}
            <button type="button" disabled={loading && !statusFilter.length} onClick={() => setShowFilters((open) => !open)} className={`tw-flex tw-items-center tw-gap-1.5 tw-rounded-lg tw-border tw-px-3 tw-py-1.5 tw-text-sm tw-font-medium hover:tw-bg-gray-50 disabled:tw-opacity-50 ${statusFilter.length ? "tw-border-primary-purple-600 tw-bg-primary-purple-50 tw-text-primary-purple-600" : "tw-border-gray-300 tw-bg-white tw-text-gray-900"}`}>
              <span className="material-symbols-outlined tw-text-xl tw-leading-none">filter_list</span>
              Filters
              {statusFilter.length > 0 && <span className="tw-rounded-full tw-bg-primary-purple-600 tw-px-1.5 tw-text-xs tw-text-white">{statusFilter.length}</span>}
            </button>
            {showFilters && <LogStatusFilter selected={statusFilter} onChange={handleStatusFilterChange} onClose={closeFilters} />}
          </div>
        </div>

        <div className="tw-mx-auto tw-max-w-[1336px] tw-px-5">{renderTable()}</div>
      </div>
    </Layout>
  );
};

export default BulkSearchLogs;
